import { buttonVariants } from "@nt/ui/recipes";
import { BarChart, ChartLegend } from "@nt/ui/charts";
import { formatMoneyTick } from "@nt/ui/format";
import { EmptyState } from "@nt/ui/states";
import { useState } from "react";
import { Link } from "react-router";

import { Section } from "@/components/section";
import { Segmented } from "@/components/segmented";
import { money } from "@/lib/format";
import type { PerformanceData } from "@/lib/types";
import { bucketLabel, count } from "@/pages/performance/format";

export type ChartMetric = "activity" | "fees";

const METRICS = [
  { value: "activity", label: "Clicks and sign-ups" },
  { value: "fees", label: "Fees earned" },
] as const;

/** Whole numbers only on the count axis: a click is never half a click. */
export const countTick = (n: number) =>
  Number.isInteger(n) ? n.toLocaleString("en-US") : "";

/**
 * The range as bars: clicks and sign-ups side by side, or fees earned. Sparse
 * daily counts read as bars; a line would spike up and down between zeros. A range
 * with nothing to draw says so instead of drawing a flat line.
 */
export function ActivityChart({
  data,
  defaultMetric = "activity",
}: {
  data: PerformanceData;
  defaultMetric?: ChartMetric;
}) {
  const [metric, setMetric] = useState<ChartMetric>(defaultMetric);
  const labels = data.series.map((b) => bucketLabel(b.date));
  const daily = data.days !== 365;
  const empty =
    metric === "fees"
      ? data.series.every((b) => b.earnedCents === 0)
      : data.series.every((b) => b.clicks === 0 && b.signups === 0);

  return (
    <Section
      title={metric === "fees" ? "Fees earned" : "Clicks and sign-ups"}
      description={
        daily ? `Day by day, last ${data.days} days.` : "Month by month."
      }
      actions={
        <Segmented
          aria-label="Chart metric"
          size="sm"
          value={metric}
          onChange={setMetric}
          options={METRICS}
        />
      }
    >
      {empty ? (
        <EmptyState
          title={
            metric === "fees"
              ? "No fees in this range"
              : "No clicks or sign-ups in this range"
          }
          description={
            metric === "fees"
              ? "You earn a fee when someone you referred places a first order. Share your link to get there."
              : "Share your link on your site, in a video or a post. Clicks show here the same day."
          }
          action={
            <Link
              to="/links"
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              Get your links
            </Link>
          }
        />
      ) : metric === "fees" ? (
        <div className="flex flex-col gap-3">
          <ChartLegend
            items={[{ label: "Fees earned", color: "chart-1", shape: "rect" }]}
          />
          <BarChart
            ariaLabel={`Fees earned, ${daily ? "day" : "month"} by ${daily ? "day" : "month"}`}
            labels={labels}
            series={[
              {
                key: "fees",
                label: "Fees earned",
                color: "chart-1",
                values: data.series.map((b) => b.earnedCents),
              },
            ]}
            formatValue={money}
            formatTick={formatMoneyTick}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ChartLegend
            items={[
              { label: "Clicks", color: "chart-1", shape: "rect" },
              { label: "Sign-ups", color: "chart-2", shape: "rect" },
            ]}
          />
          <BarChart
            ariaLabel={`Clicks and sign-ups, ${daily ? "day" : "month"} by ${daily ? "day" : "month"}`}
            labels={labels}
            series={[
              {
                key: "clicks",
                label: "Clicks",
                color: "chart-1",
                values: data.series.map((b) => b.clicks),
              },
              {
                key: "signups",
                label: "Sign-ups",
                color: "chart-2",
                values: data.series.map((b) => b.signups),
              },
            ]}
            formatValue={count}
            formatTick={countTick}
          />
        </div>
      )}
    </Section>
  );
}
